import { addFramingFlowEntry } from '@framebridge/toolbox/../app/javascript/domains/DataLayer.js';
import fetchStoresData from './fetch-stores-data';
import { canRequestGeolocation, requestGeolocation } from './get-cached-geolocation';

const DISTANCE_THRESHOLD = 75;

// Carry all query params from the current URL through to the framing tile
// links so selections made before landing here (e.g. product, moulding, mat
// from a PDP) aren't lost on the way into the framing flow.
function carryUrlParams() {
  const params = new URLSearchParams(window.location.search);
  if (!params.toString()) return;

  const $links = document.querySelectorAll('.wayf-framing .wayf-tile__link[href]');
  $links.forEach(($link) => {
    try {
      const url = new URL($link.href, window.location.origin);
      params.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
      $link.href = url.toString();
    } catch (err) {
      // Skip links whose href can't be parsed.
    }
  });
}

// Each group is a flex sibling stretched to a shared height, then split into a
// header row (auto) and a tile row (1fr). When group headers differ in height
// the 1fr rows differ, so a group with a shorter header gets a taller tile.
// Equalize the headers and the stretch keeps every tile the same height with
// aligned tops. Which header is tallest flips with viewport width (narrower
// columns wrap copy to more lines), so this can't be a static CSS min-height.
function equalizeGroupHeaders() {
  document.querySelectorAll('.wayf-framing').forEach(($section) => {
    const $groups = $section.querySelector('.wayf-framing__groups');
    const $headers = [
      ...$section.querySelectorAll('.wayf-group__header:not(.wayf-group__header--empty)'),
    ];

    // Reset first so a re-run (resize, store content load) measures natural height.
    $headers.forEach(($h) => { $h.style.minHeight = ''; });

    // Groups stack vertically on mobile — only equalize when side by side.
    if (!$groups || $headers.length < 2 || getComputedStyle($groups).flexDirection === 'column') {
      return;
    }

    const tallest = Math.max(...$headers.map(($h) => $h.getBoundingClientRect().height));
    $headers.forEach(($h) => { $h.style.minHeight = `${tallest}px`; });
  });
}

function wayfFraming() {
  if (document.querySelector('.wayf-framing')) {
    addFramingFlowEntry(`${window.location.pathname}${window.location.search}`);
  }

  carryUrlParams();

  // Run on load, after webfonts settle (they change header height), and on resize.
  equalizeGroupHeaders();
  if (document.fonts?.ready) document.fonts.ready.then(equalizeGroupHeaders);
  let rafId;
  window.addEventListener('resize', () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(equalizeGroupHeaders);
  });

  const $storeEls = document.querySelectorAll('.js-wayf-store');
  if (!$storeEls.length) return;

  const storage = window.sessionStorage;

  function buildNear(store) {
    $storeEls.forEach(($el) => {
      const $desc = $el.querySelector('.js-wayf-store-description');
      const $links = $el.querySelector('.js-wayf-store-links');

      if (!$desc || !$links) return;

      $desc.innerHTML = `Your Nearest Store: <a class="wayf-store__store-link" href="${store.website}">${store.name}</a>`;
      $links.innerHTML = '';
    });
  }

  function buildDefault(count) {
    $storeEls.forEach(($el) => {
      const $desc = $el.querySelector('.js-wayf-store-description');
      const $links = $el.querySelector('.js-wayf-store-links');
      const { storeLocatorUrl } = $el.dataset;

      if (!$desc || !$links) return;

      $desc.innerHTML = count
        ? `We're not quite in your neighborhood yet. Check out our other ${count} locations.`
        : '';
      $links.innerHTML = storeLocatorUrl
        ? `<ul class="wayf-store__list"><li class="wayf-store__listitem"><a class="wayf-store__link" href="${storeLocatorUrl}">Find Your Nearest Store</a></li></ul>`
        : '';
    });
  }

  function applyResult(nearestStore, allStores) {
    if (nearestStore && nearestStore.distance <= DISTANCE_THRESHOLD) {
      buildNear(nearestStore);
    } else {
      buildDefault(allStores?.length);
    }
    // Store copy lives in the locator's host header — its height just changed.
    equalizeGroupHeaders();
    addUseMyLocation();
  }

  async function addUseMyLocation() {
    if (!(await canRequestGeolocation())) return;

    const item = '<li class="wayf-store__listitem"><button class="wayf-store__link wayf-store__location-btn" type="button">Use my location</button></li>';

    $storeEls.forEach(($el) => {
      const $links = $el.querySelector('.js-wayf-store-links');
      if (!$links) return;

      const $list = $links.querySelector('.wayf-store__list');
      if ($list) {
        $list.insertAdjacentHTML('beforeend', item);
      } else {
        $links.innerHTML = `<ul class="wayf-store__list">${item}</ul>`;
      }

      const $button = $links.querySelector('.wayf-store__location-btn');
      $button.addEventListener('click', async () => {
        $button.disabled = true;
        const coords = await requestGeolocation();

        if (!coords) {
          $button.parentElement.remove();
          equalizeGroupHeaders();
          return;
        }

        storage?.removeItem('nearestStoresData');
        const result = await fetchStoresData();
        applyResult(result?.nearestStore, result?.stores);
      });
    });
    equalizeGroupHeaders();
  }

  // Use data already in sessionStorage if available
  const cached = storage?.getItem('nearestStoresData');
  if (cached) {
    try {
      const stores = JSON.parse(cached);
      applyResult(stores?.[0], stores);
    } catch (e) {
      // malformed cache — fall through to fetch
    }
    return;
  }

  // Also listen for the event that store-locator.js dispatches (fires on footer intersect)
  let resolved = false;
  window.addEventListener('nearestStoresUpdated', (e) => {
    if (resolved) return;
    resolved = true;
    const stores = e.detail?.stores || [];
    applyResult(stores[0], stores);
  });

  // Trigger our own fetch when the section scrolls into view so we don't
  // depend on the user reaching the footer first
  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      obs.unobserve(entry.target);
      if (resolved) return;

      fetchStoresData().then((result) => {
        if (resolved) return;
        resolved = true;
        applyResult(result?.nearestStore, result?.stores);
      });
    });
  }, { rootMargin: '200px' });

  $storeEls.forEach(($el) => observer.observe($el));
}

export default wayfFraming;
